import React from "react";
import { cn } from "@/lib/utils";

/**
 * OddsBadge - Standardized odds display component
 * 
 * Props:
 * - value: number (e.g., 2.0, 1.83, 3.25)
 * - mode: "LIVE" | "FIXED" (default: "LIVE")
 * - direction: "UP" | "DOWN" | "SAME" | null (for live odds movement indicator)
 * - size: "sm" | "md" | "lg" (default: "md")
 * - className: additional CSS classes
 */
export function OddsBadge({ 
  value, 
  mode = "LIVE", 
  direction = null,
  size = "md",
  className 
}) {
  if (value === null || value === undefined) {
    return null;
  }

  const formattedValue = `x${value.toFixed(2)}`;
  
  const sizeClasses = {
    sm: "text-sm px-2 py-0.5",
    md: "text-base px-3 py-1",
    lg: "text-xl px-4 py-1.5 font-bold",
  };

  const directionIndicator = {
    UP: "↑",
    DOWN: "↓",
    SAME: "",
  };

  const directionColors = {
    UP: "text-green-400",
    DOWN: "text-red-400",
    SAME: "",
  };

  return (
    <span 
      className={cn(
        "inline-flex items-center gap-1 rounded-md font-mono",
        "bg-black/30 border border-white/20",
        mode === "LIVE" ? "text-yellow-300" : "text-blue-300",
        sizeClasses[size],
        className
      )}
    >
      {formattedValue}
      {direction && direction !== "SAME" && (
        <span className={cn("text-xs", directionColors[direction])}>
          {directionIndicator[direction]}
        </span>
      )}
    </span>
  );
}

/**
 * CoinDisplay - Standardized coin amount display
 */
export function CoinDisplay({ 
  amount, 
  showSign = false,
  size = "md",
  className 
}) {
  const isPositive = amount > 0;
  const isNegative = amount < 0;
  const displayAmount = Math.abs(amount);
  
  const sizeClasses = {
    sm: "text-sm",
    md: "text-base",
    lg: "text-xl font-bold",
  };

  const colorClass = showSign 
    ? isPositive 
      ? "text-green-400" 
      : isNegative 
        ? "text-red-400" 
        : "text-white"
    : "text-white";

  const sign = showSign && isPositive ? "+" : showSign && isNegative ? "-" : "";

  return (
    <span className={cn("font-mono inline-flex items-center gap-1", sizeClasses[size], colorClass, className)}>
      {sign}{displayAmount.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
      <span className="text-yellow-400">🪙</span>
    </span>
  );
}

/**
 * WalletSummary - Compact wallet display
 */
export function WalletSummary({ 
  balance, 
  reserved, 
  available,
  showDetails = true,
  className 
}) {
  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <div className="flex items-center justify-between">
        <span className="text-white/70 text-sm">Kontostand</span>
        <CoinDisplay amount={balance} size="lg" />
      </div>
      {showDetails && (
        <>
          <div className="flex items-center justify-between text-sm">
            <span className="text-white/50">Verfügbar</span>
            <CoinDisplay amount={available} size="sm" />
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-white/50">Reserviert</span>
            <CoinDisplay amount={reserved} size="sm" />
          </div>
        </>
      )}
    </div>
  );
}

export default OddsBadge;
